import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../services/api';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function TenantAuthPage() {
  const { tenantSlug: routeTenantSlug } = useParams();
  const { user, login, register, setTenantSlug, tenantSlug: authTenantSlug } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [emailError, setEmailError] = useState('');
  const tenantSlug = routeTenantSlug || authTenantSlug;
  const tenantPrefix = routeTenantSlug ? `/${routeTenantSlug}` : '';

  useEffect(() => {
    if (tenantSlug) setTenantSlug(tenantSlug);
    fetchTenant();
  }, [tenantSlug]);

  useEffect(() => {
    if (user && tenant) {
      if (['admin', 'tenant_admin', 'support_agent', 'finance_staff', 'moderator', 'support'].includes(user.role)) {
        navigate(`${tenantPrefix}/admin`);
      } else if (user.role === 'referral_agent') {
        navigate(`/referral-staff`);
      } else {
        navigate(`${tenantPrefix}/app`);
      }
    }
  }, [user, tenant, tenantPrefix, tenantSlug]);

  const fetchTenant = async () => {
    try {
      if (tenantSlug) {
        const res = await api.get(`/api/tenants/public/${tenantSlug}`);
        setTenant(res.data);
      } else {
        const res = await api.get('/api/tenant/current');
        if (!res.data?.tenant) throw new Error('No tenant for this host');
        setTenant(res.data.tenant);
        setTenantSlug(res.data.tenant.slug);
      }
    } catch (err) {
      toast.error('Invalid tenant URL');
      navigate('/auth');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    if (!email || !password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setAuthLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password, tenantSlug);
        toast.success('Welcome back!');
      } else {
        if (!fullName) {
          toast.error('Please enter your full name');
          setAuthLoading(false);
          return;
        }
        await register(email, password, fullName, null, referralCode, tenantSlug);
        toast.success('Account created successfully!');
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Backend returns 'message' field in contract-compliant error envelope
      const backendMessage = err.response?.data?.message;
      const message = typeof detail === 'string' ? detail :
                      typeof backendMessage === 'string' ? backendMessage :
                      'Authentication failed';
      if (mode === 'register' && status === 409 && /email/i.test(message)) {
        setEmailError(message);
        toast.error(message);
      } else {
        toast.error(message);
      }
    } finally {
      setAuthLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E14' }}>
        <div className="spinner" />
      </div>
    );
  }

  const branding = tenant?.branding || {};
  const pc = branding.primary_color || '#D4AF37';
  const bgColor = branding.background_color || '#0B0E14';
  const cardBg = branding.card_color || '#131822';
  const resolveUrl = (url) => url?.startsWith('http') ? url : `${API_BASE}${url}`;

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: bgColor }}>
      <div className="w-full max-w-md">
        {/* Tenant Branding Header */}
        <div className="text-center mb-8">
          {branding.logo_url ? (
            <img src={resolveUrl(branding.logo_url)} alt={tenant.name} className="h-16 mx-auto mb-4 rounded-xl" />
          ) : (
            <div className="w-16 h-16 rounded-xl mx-auto mb-4 flex items-center justify-center text-2xl font-bold"
              style={{ background: `${pc}20`, color: pc }}>
              {tenant?.name?.charAt(0).toUpperCase()}
            </div>
          )}
          <h1 className="text-2xl font-bold" style={{ color: pc }}>{tenant?.name}</h1>
          <p className="text-gray-400 text-sm mt-1">Powered by Vegas Lobby</p>
        </div>

        {/* Login Card - uses tenant theme colors */}
        <div className="rounded-2xl p-8 border" style={{ background: cardBg, borderColor: `${pc}22` }}>
          <h2 className="text-xl font-semibold text-center mb-6 text-white">
            {mode === 'login' ? 'Sign In' : 'Create Account'}
          </h2>

          {/* Tabs */}
          <div className="flex rounded-lg p-1 mb-6" style={{ background: bgColor }}>
            <button onClick={() => setMode('login')}
              className="flex-1 py-2 rounded-md text-sm font-medium transition-all"
              style={{ background: mode === 'login' ? pc : 'transparent', color: mode === 'login' ? '#000' : '#9CA3AF' }}>
              Sign In
            </button>
            <button onClick={() => setMode('register')}
              className="flex-1 py-2 rounded-md text-sm font-medium transition-all"
              style={{ background: mode === 'register' ? pc : 'transparent', color: mode === 'register' ? '#000' : '#9CA3AF' }}>
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name" className="pl-10 h-11 text-white"
                    style={{ background: bgColor, borderColor: `${pc}33` }} />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input type="email" value={email}
                  onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                  placeholder="Enter your email"
                  className={`pl-10 h-11 text-white ${emailError ? 'border-red-500' : ''}`}
                  style={{ background: bgColor, borderColor: emailError ? '#ef4444' : `${pc}33` }}
                  data-testid="tenant-email-input"
                  aria-invalid={!!emailError}
                />
              </div>
              {emailError && (
                <p className="mt-1.5 text-xs text-red-400" data-testid="tenant-email-error">
                  {emailError}
                  {mode === 'register' && /in use|registered/i.test(emailError) && (
                    <button type="button" onClick={() => { setMode('login'); setEmailError(''); }}
                      className="underline ml-1 hover:text-red-300" data-testid="tenant-switch-to-login-link">
                      Sign in instead →
                    </button>
                  )}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password" className="pl-10 pr-10 h-11 text-white"
                  style={{ background: bgColor, borderColor: `${pc}33` }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {mode === 'register' && (
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Referral Code (Optional)</label>
                <Input type="text" value={referralCode} onChange={(e) => setReferralCode(e.target.value)}
                  placeholder="Enter referral code" className="h-11 text-white"
                  style={{ background: bgColor, borderColor: `${pc}33` }} />
              </div>
            )}

            <Button type="submit" disabled={authLoading} className="w-full h-11 font-semibold transition-all"
              style={{ background: pc, color: '#000' }}>
              {authLoading ? (
                <div className="spinner w-5 h-5 border-2 border-black/30 border-t-black" />
              ) : (
                <>{mode === 'login' ? 'Sign In' : 'Create Account'}<ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
